import { NumericFormat } from "react-number-format";
import { MiControlledTextField } from "../MiControlledTextField/MiControlledTextField";

/**
 * A controlled numeric input component that formats numbers using react-number-format.
 * Wraps the MiControlledTextField component to provide additional formatting options.
 *
 * @param {Object} props - Component props.
 * @param {boolean|string} [props.thousandSeparator] - Character or boolean to enable thousand separators.
 * @param {number} [props.decimalScale] - Number of decimal places to allow.
 * @param {boolean} [props.fixedDecimalScale] - Whether to always show the fixed number of decimal places.
 * @param {boolean} [props.allowNegative] - Whether to allow negative numbers.
 * @param {boolean} [props.allowLeadingZeros] - Whether to allow leading zeros.
 * @param {string} [props.prefix] - String to prefix the value with.
 * @param {string} [props.suffix] - String to suffix the value with.
 * @param {string} [props.name] - Name attribute for the input.
 * @param {boolean} [props.disabled] - Whether the input is disabled.
 * @param {Object} [props.formControl] - Form control object for integration with form libraries.
 * @param {function} [props.onValueChange] - Callback fired when the value changes.
 * @param {string|number} [props.width] - Width of the input.
 * @param {boolean} [props.required] - Whether the input is required.
 * @param {string|number} [props.padding] - Padding for the input.
 * @param {string|number} [props.minWidth] - Minimum width of the input.
 * @param {number} [props.flexGrow] - Flex grow property for layout.
 * @param {string} [props.label] - Label for the input.
 * @param {string} [props.placeholder] - Placeholder text for the input.
 * @param {string|number} [props.value] - Value of the input.
 * @param {boolean} [props.readOnly] - Whether the input is read-only.
 * @param {string} [props.className] - Additional class names for styling.
 *
 * @returns {JSX.Element} The formatted numeric input component.
 */
export const MiControlledNumericInput = ({
  thousandSeparator,
  decimalScale,
  fixedDecimalScale,
  allowNegative,
  allowLeadingZeros,
  prefix,
  suffix,
  name,
  disabled,
  formControl,
  onValueChange,
  width,
  required,
  padding,
  minWidth,
  flexGrow,
  label,
  placeholder,
  value,
  readOnly,
  InputProps,
  sx,
  error,
  helperText,
  className,
}) => {
  return (
    <NumericFormat
      customInput={MiControlledTextField}
      thousandSeparator={thousandSeparator}
      decimalScale={decimalScale}
      fixedDecimalScale={fixedDecimalScale}
      allowNegative={allowNegative}
      allowLeadingZeros={allowLeadingZeros}
      prefix={prefix}
      suffix={suffix}
      name={name}
      disabled={disabled}
      formControl={formControl}
      onValueChange={onValueChange}
      width={width}
      required={required}
      padding={padding}
      minWidth={minWidth}
      flexGrow={flexGrow}
      label={label}
      placeholder={placeholder}
      value={value}
      readOnly={readOnly}
      InputProps={InputProps}
      sx={sx}
      error={error}
      helperText={helperText}
      className={className}
    />
  );
};
